import React from "react";
import Stack from "@mui/material/Stack";
import styles from "./styles";
import MatchingStatus from "./MatchingStatus";
import MatchCard from "./MatchCard";
import { useMatching } from "../MatchingProvider";
import { Button, Typography } from "@mui/material";

const Wrapper = ({ numMatches, children, onRefresh }) => (
  <Stack component="section" sx={styles}>
    <Typography variant="body2" pb={7}>
      Review and approve recommended therapy schedules for clinicians. Matching results update each
      day.
    </Typography>
    <Stack direction="row" justifyContent="space-between" gap={3} alignItems="center">
      <Stack direction="row" gap={3}>
        <Typography color="text.secondary" variant="body2">
          Matching Results {numMatches > 0 && `(${numMatches})`}
        </Typography>
      </Stack>
      <Stack direction="row" gap={3}>
        <Button variant="text" size="small" onClick={onRefresh}>
          Refresh
        </Button>
      </Stack>
    </Stack>
    {children}
  </Stack>
);

/**
 * The MatchPanel component conditionally renders a list of generated schedules or a message indicating
 * no schedules were found, along with a refresh button.
 * @param {Object} props - The props object.
 * @returns {JSX.Element} - The MatchPanel component.
 */
const MatchPanel = ({ setShowCaseload, onRefresh }) => {
  const {
    generatedSchedules,
    generatedSchedulesLoading,
    setSelectedClinician,
    noMatchesCreated,
    isRefreshingProposed,
  } = useMatching();

  // auto-select the first clinician when the data is loaded for the first time
  const initialDataLoadedRef = React.useRef(false);
  React.useEffect(() => {
    if (generatedSchedules?.length && !initialDataLoadedRef.current) {
      initialDataLoadedRef.current = true;
      setSelectedClinician(generatedSchedules[0].clinician_id);
    }
  }, [generatedSchedules]);

  if (generatedSchedulesLoading) {
    return (
      <Wrapper>
        <Stack direction="column" spacing={2} justifyContent="center" flexGrow={1}>
          <MatchingStatus.Loading />
        </Stack>
      </Wrapper>
    );
  }

  if (noMatchesCreated) {
    return (
      <Wrapper>
        <Stack direction="column" spacing={2} justifyContent="center" height="100%">
          <MatchingStatus.NoFound />
        </Stack>
      </Wrapper>
    );
  }

  if (isRefreshingProposed) {
    return (
      <Wrapper>
        <Stack direction="column" spacing={2} justifyContent="center" height="100%">
          <MatchingStatus.Refreshing />
        </Stack>
      </Wrapper>
    );
  }

  return (
    // TODO: Pass in onRefresh function
    <Wrapper numMatches={generatedSchedules.length} onRefresh={onRefresh}>
      {generatedSchedules.length ? (
        <Stack gap={5}>
          {generatedSchedules?.map((generatedSchedule) => (
            <MatchCard
              key={generatedSchedule.clinician_id}
              generatedSchedule={generatedSchedule}
              setShowCaseload={setShowCaseload}
            />
          ))}
        </Stack>
      ) : (
        <Stack direction="column" spacing={2} justifyContent="center" flexGrow={1}>
          <MatchingStatus.Empty />
        </Stack>
      )}
    </Wrapper>
  );
};

export default MatchPanel;
